import { GoogleAds } from '@sgcmpteltd/uikit-next';
import type {
  OnSlotRenderEnded,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import { IosArrowRight } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import dayjs from 'dayjs';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useContext, useMemo, useRef } from 'react';

import { TextLink } from '@/components/text-link';
import {
  newCarCompetitorPromotion as newCarCompetitorPromotionGoogleAdsConfig,
  newCarPromotion as newCarPromotionGoogleAdsConfig,
} from '@/configs/google/ads/desktop';
import { InfoContext } from '@/context/info';
import { joinSgcmSite } from '@/utils/url/join/public';

import { ShareUrlButton } from '../../../share-url-button';
import {
  GOOGLE_ADS_TARGETING_KEY_NEW_CAR_COMPETITOR,
  GOOGLE_ADS_TARGETING_KEY_NEW_CAR_PROMOTION,
  PROMOTION_MORE_PROMOS_ICON_SIZE,
  PROMOTION_POSTED_ON_DATE_FORMAT,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const Content: Component = ({
  dealer: {
    id: dealerId,
  },
  id,
  isAllModels,
  models,
  endsOn,
  postedOn,
  link,
  make,
  competitorCars,
  onErrorReport,
  setOpen,
}) => {
  const infoContext = useContext(InfoContext);

  const isCompetitorCar = competitorCars?.find(
    // infoContext may not exist, it only exists in info page
    competitorCar => competitorCar === infoContext?.carDetail?.id.toString(),
  );
  const onSlotRenderEndedRef = useRef<OnSlotRenderEnded>(({
    isEmpty,
    slot,
  }) => {
    if (!isEmpty) {
      return;
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([
        slot,
      ]);
    });
  });
  const targetingMap = useMemo(() => {
    const result: Record<string, string | string[]> = {
      [GOOGLE_ADS_TARGETING_KEY_NEW_CAR_PROMOTION]: [
        `${id}`,
      ],
    };

    if (isCompetitorCar && competitorCars) {
      result[GOOGLE_ADS_TARGETING_KEY_NEW_CAR_COMPETITOR] = competitorCars;
    }

    return result;
  }, [id, isCompetitorCar, competitorCars]);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleMorePromosClick = (e: React.MouseEvent) => {
    if (pathname === '/promotions') {
      e.preventDefault();
      setOpen?.(false);
      const params = new URLSearchParams(searchParams.toString());

      params.delete('page');
      params.delete('pid');
      params.set('active_tab', 'all');
      params.set('brand', make || '');

      router.push(`?${params.toString()}`);
    } else {
      window.open(`${joinSgcmSite('/new-cars/promotions')}`, '_self');
    }
  };

  return (
    <div className={styles.containerRoot}>
      <div
        className={styles.containerPromotionContent}>
        <div className={styles.containerPromotionTitleContent}>
          <div className={styles.containerTitleContentDetail}>
            <div
              className={styles.containerGoogleAds}>
              <GoogleAds
                adUnitPath={
                  isCompetitorCar
                    ? newCarCompetitorPromotionGoogleAdsConfig.adUnitPath
                    : newCarPromotionGoogleAdsConfig.adUnitPath
                }
                divId={
                  isCompetitorCar
                    ? newCarCompetitorPromotionGoogleAdsConfig.divId
                    : newCarPromotionGoogleAdsConfig.divId
                }
                slot={
                  isCompetitorCar
                    ? newCarCompetitorPromotionGoogleAdsConfig.slot
                    : newCarPromotionGoogleAdsConfig.slot
                }
                targetingMap={targetingMap}
                onSlotRenderEndedRef={onSlotRenderEndedRef} />
            </div>
            <div className={styles.containerApplicable}>
              <div className={styles.textApplicable}>Applicable Models</div>
              { isAllModels
                ? (
                  <ul
                    className={styles.listPromotionModelLink}>
                    <li
                      key="all models"
                      className={styles.linkPromotionModel}>
                      <TextLink
                        href={`${joinSgcmSite(`/new_cars/newcars_listing.php?DID=${dealerId}`)}`}
                        target="_self"
                        text={`All ${make} Models`} />
                    </li>
                  </ul>
                )
                : (
                  <ul
                    className={styles.listPromotionModelLink}>
                    {
                      models.map(({ title, link: modelLink }) => (
                        <li
                          key={modelLink}
                          className={styles.linkPromotionModel}>
                          <TextLink
                            href={modelLink}
                            text={title} />
                        </li>
                      ))
                    }
                  </ul>
                )}

            </div>
            <div
              className={styles.containerPromotionDate}>
              <span className={styles.textRed}>
                {endsOn}
              </span>
              <span className={styles.textGray}>
                Posted on:
                {' '}
                <time>
                  {dayjs(postedOn).format(PROMOTION_POSTED_ON_DATE_FORMAT)}
                </time>
              </span>
            </div>
            <div className={styles.containerMorePromoAndShare}>
              <button
                className={styles.morePromosLink}
                onClick={handleMorePromosClick}>
                More Promos
                <IosArrowRight
                  size={PROMOTION_MORE_PROMOS_ICON_SIZE} />
              </button>
              <div
                className={styles.containerShareLink}>
                <ShareUrlButton
                  link={link} />
              </div>
            </div>
          </div>
        </div>
        <span
          className={cx(
            styles.textFooter,
            styles.textDisclaimer,
          )}>
          Disclaimer: Dealer
          {'\''}
          s Terms & Conditions applies on this promotion. Sgcarmart.com doesn
          {'\''}
          t
          {
            '\n'
          }
          assume any responsibility for errors or omissions or warrant the
          accuracy of this information.
        </span>
        <button
          className={styles.textReportError}
          onClick={() => {
            onErrorReport?.();
          }}>
          Report Error
        </button>
      </div>
    </div>
  );
};
